import React from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Chip } from '@wartek-id/chip'
import { Text } from '@wartek-id/text-v1'
import { Select } from '@wartek-id/select'
import { useToast } from '@wartek-id/toast-v2'

import { generateExcelFile } from 'utils/excel'
import {
  PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS,
  PENETAPAN_PEGAWAI_STATE_LABELS,
  PENETAPAN_PEGAWAI_FILTER_LABELS,
} from 'app/KinerjaTimPenilaiPSPage/constants/penetapan'
import { PERFORMANCE_PRACTICE_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/performancePractice'
import { WORK_BEHAVIOR_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/workBehavior'
import { formatDate } from 'utils/datetime'

import type {
  PenetapanPegawaiFilters,
  PenetapanPegawaiList,
  PenetapanPegawaiCountStatus,
} from 'types/kinerja-tim-penilai-ps'

const pageLimits = [10, 25, 50, 100]

type FilterProps = {
  data: PenetapanPegawaiList
  filters: PenetapanPegawaiFilters
  onSetFilters: (filters: PenetapanPegawaiFilters) => void
}

export const Filters = ({ data, filters, onSetFilters }: FilterProps) => {
  const toast = useToast()

  const downloadExcelFile = () => {
    generateExcelFile({
      rows: (data?.pengawasSekolah ?? []).map((row) => ({
        Nama: row.namaPengawas,
        NIP: row.nip,
        Instansi: row.dinasPendidikan,
        'Rating Praktik Kinerja':
          PERFORMANCE_PRACTICE_RESULT_TEXT?.[
            row?.predikatKinerjaTk?.praktikKinerjaRating
          ] ?? '-',
        'Rating Perilaku Kerja':
          WORK_BEHAVIOR_RESULT_TEXT?.[
            row?.predikatKinerjaTk?.perilakuKerjaRating
          ] ?? '-',
        'Predikat Kinerja':
          PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS?.[
            row?.predikatKinerjaTk?.predikatKinerja
          ] ?? '-',
        'Status Pengiriman':
          PENETAPAN_PEGAWAI_STATE_LABELS?.[row?.status] ?? '-',
      })),
      title: `Data Penetapan Predikat Kinerja Pegawai - Pengawas Sekolah - ${formatDate(
        new Date(),
        'ddmmyyyy'
      )}`,
    })
    toast({
      message:
        'Berhasil unduh data Penetapan Predikat Kinerja dalam format Excel',
      type: 'success',
    })
  }

  return (
    <div className="flex justify-end gap-x-4">
      <Select
        onChange={(e) => {
          onSetFilters?.({
            ...filters,
            limit: parseInt(e.target.value, 10),
          })
        }}
        value={filters?.limit}
        className="!text-sm !py-3"
      >
        {pageLimits.map((value, index) => (
          <option key={index} value={value}>
            {value} Nama
          </option>
        ))}
      </Select>
      <Select
        onChange={(e) => {
          onSetFilters?.({
            ...filters,
            predikatKinerja: e.target
              .value as PenetapanPegawaiFilters['predikatKinerja'],
          })
        }}
        value={filters?.predikatKinerja}
        className="!text-sm !py-3"
      >
        <option value="">Semua Predikat Kinerja</option>
        {Object.entries(PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS).map(
          ([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          )
        )}
      </Select>
      <Select
        onChange={(e) => {
          onSetFilters?.({
            ...filters,
            status: e.target.value as PenetapanPegawaiFilters['status'],
          })
        }}
        value={filters?.status}
        className="!text-sm !py-3"
      >
        <option value="">Semua Status Pengiriman</option>
        {Object.entries(PENETAPAN_PEGAWAI_FILTER_LABELS)
          .filter(([key]) => ['TK_NOT_SENT', 'TK_SENT'].includes(key))
          .map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
      </Select>
      <Button
        onClick={downloadExcelFile}
        color="white"
        className="!w-30 flex-none !text-sm"
      >
        Unduh data
      </Button>
    </div>
  )
}

type StatusFilterPPKProps = {
  activeStatus: string
  setActiveStatus: (status: string) => void
  countData: PenetapanPegawaiCountStatus
}

export const StatusFilterPPK = ({
  activeStatus,
  setActiveStatus,
  countData,
}: StatusFilterPPKProps) => {
  const statuses = {
    ALL: {
      label: 'Semua',
    },
    PPK_NOT_SENT: {
      keys: ['sentToPpk'],
      label: 'Belum dikirim',
    },
    PPK_SENT: {
      keys: ['sendingToBkn', 'sentToBkn', 'errorSend'],
      label: 'Sudah dikirim',
    },
    PPK_NOT_RATED: {
      keys: ['notSent', 'notRated'],
      label: 'Belum selesai dinilai',
    },
  }

  const total = Object.values(countData?.statusCount ?? {}).reduce(
    (acc, curr) => acc + (typeof curr === 'number' ? curr : 0),
    0
  )

  return (
    <div className="flex mt-4 gap-x-2">
      {Object.keys(statuses).map((key, index) => {
        const isActive = activeStatus === key
        let count = 0
        if (statuses[key]?.keys) {
          count = statuses[key].keys.reduce(
            (acc, k) => acc + (countData?.statusCount?.[k] ?? 0),
            0
          )
        }

        if (key === 'ALL') {
          count = total
        }

        return (
          <button key={index} value={key} onClick={() => setActiveStatus(key)}>
            <Chip
              data-testid="conversation-message-feedback-option-item"
              isActive={isActive}
              className={clsx(
                isActive
                  ? '!border-interactive-default !bg-blue-5'
                  : '!border-default bg-white',
                'focus:!ring-0'
              )}
            >
              <Text
                className={clsx(isActive ? '!text-blue-70' : '!text-default')}
                variant="action"
              >
                {statuses[key]?.label} ({count})
              </Text>
            </Chip>
          </button>
        )
      })}
    </div>
  )
}
